/**
 * Video Proof — Upload and manage lift verification videos.
 *
 * Flow:
 * 1. User records/picks video via expo-image-picker (supports video)
 * 2. Upload to Supabase Storage bucket "lift-videos"
 * 3. Store public URL in strength_events.video_url
 * 4. Display "📹 Verified" badge on lifts with video
 */

import { supabase } from './supabase';
import * as ImagePicker from 'expo-image-picker';
import { Platform } from 'react-native';
import { createLogger } from './logger';

const log = createLogger('VIDEO_PROOF');

const BUCKET = 'lift-videos';
const MAX_SIZE_MB = 50;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

export interface VideoUploadResult {
  url: string | null;
  error: string | null;
}

/**
 * Launch the device video picker (camera roll or record).
 * Returns the local URI of the selected video, or null if cancelled.
 */
export async function pickVideo(): Promise<string | null> {
  const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
  if (!permission.granted) {
    return null;
  }

  const result = await ImagePicker.launchImageLibraryAsync({
    mediaTypes: 'videos',
    allowsEditing: true,
    videoMaxDuration: 60, // 60 seconds max
    quality: 0.7,
  });

  if (result.canceled || !result.assets?.[0]?.uri) {
    return null;
  }

  return result.assets[0].uri;
}

/**
 * Record a video using the device camera.
 * Returns the local URI of the recorded video, or null if cancelled.
 */
export async function recordVideo(): Promise<string | null> {
  const permission = await ImagePicker.requestCameraPermissionsAsync();
  if (!permission.granted) {
    return null;
  }

  const result = await ImagePicker.launchCameraAsync({
    mediaTypes: 'videos',
    allowsEditing: true,
    videoMaxDuration: 60,
    quality: 0.7,
  });

  if (result.canceled || !result.assets?.[0]?.uri) {
    return null;
  }

  return result.assets[0].uri;
}

/**
 * Upload a video to Supabase Storage and return the public URL.
 */
const FREE_VIDEO_LIMIT = 5;

/**
 * Check if a free-tier user has exceeded their video proof limit.
 * Pro+ users are unlimited.
 */
export async function checkVideoLimit(
  userId: string,
  tier: string,
): Promise<{ allowed: boolean; error: string | null }> {
  if (tier !== 'free') return { allowed: true, error: null };

  const { count, error } = await supabase
    .from('strength_events')
    .select('id', { count: 'exact', head: true })
    .eq('profile_id', userId)
    .not('video_url', 'is', null);

  if (error) {
    log.error('Video limit check failed', error);
    return { allowed: true, error: null }; // fail open
  }

  if ((count ?? 0) >= FREE_VIDEO_LIMIT) {
    return {
      allowed: false,
      error: `Free accounts can store up to ${FREE_VIDEO_LIMIT} video proofs. Upgrade to Pro for unlimited.`,
    };
  }
  return { allowed: true, error: null };
}

export async function uploadLiftVideo(
  userId: string,
  localUri: string,
): Promise<VideoUploadResult> {
  try {
    const timestamp = Date.now();
    const ext = localUri.split('.').pop() || 'mp4';
    const path = `${userId}/${timestamp}.${ext}`;

    // Fetch the file as a blob
    let fileBody: Blob | ArrayBuffer;
    if (Platform.OS === 'web') {
      const response = await fetch(localUri);
      fileBody = await response.blob();
    } else {
      const response = await fetch(localUri);
      fileBody = await response.blob();
    }

    // Check file size
    if (fileBody instanceof Blob && fileBody.size > MAX_SIZE_BYTES) {
      return { url: null, error: `Video too large. Maximum size is ${MAX_SIZE_MB}MB.` };
    }

    const { error: uploadError } = await supabase.storage
      .from(BUCKET)
      .upload(path, fileBody, {
        contentType: `video/${ext === 'mov' ? 'quicktime' : ext}`,
        upsert: false,
      });

    if (uploadError) {
      log.error('Upload failed', uploadError);
      return { url: null, error: uploadError.message };
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from(BUCKET)
      .getPublicUrl(path);

    return { url: urlData.publicUrl, error: null };
  } catch (err: any) {
    log.error('Video upload error', err);
    return { url: null, error: err.message || 'Failed to upload video' };
  }
}

/**
 * Attach a video URL to a strength event.
 */
export async function attachVideoToEvent(
  eventId: string,
  videoUrl: string,
): Promise<{ success: boolean; error: string | null }> {
  const { error } = await supabase
    .from('strength_events')
    .update({ video_url: videoUrl })
    .eq('id', eventId);

  if (error) {
    return { success: false, error: error.message };
  }
  return { success: true, error: null };
}

/**
 * Check if a strength event has video proof.
 */
export async function hasVideoProof(eventId: string): Promise<boolean> {
  const { data } = await supabase
    .from('strength_events')
    .select('video_url')
    .eq('id', eventId)
    .single();

  return Boolean(data?.video_url);
}
